import React, { useMemo, useState } from "react";
import { FC } from "react";
import { useEffect } from "react";
import ClearDataButton from "./ClearDataButton";
import { useRef } from "react";
import { ArchiveIcon, CubeIcon, UserIcon } from "@heroicons/react/outline";
import { debounce } from "lodash";
import { useFetch } from "hooks/useFetch";
import { Link } from "react-router-dom";
import logging from "utils/logging";
import { eventAnalytic } from "hooks/useAnalytics";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { RentripsURL } from "utils/request";

export interface ThingInputPropos {
	defaultValue: string;
	onChange?: (value: string) => void;
	onInputDone?: (value: string) => void;
	placeHolder?: string;
	desc?: string;
	className?: string;
	autoFocus?: boolean;
}

const ThingInput: FC<ThingInputPropos> = ({
	defaultValue,
	autoFocus = false,
	onChange,
	onInputDone,
	placeHolder = "Location",
	desc = "Where are you going?",
	className = "nc-flex-1.5",
}) => {
	const { userInfo } = useSelector((state: RootState) => state.userInfo);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [dValue, setDValue] = useState("-");

	const { data } = useFetch<{
		products: { _id: string; name: string }[];
		users: { _id: string; name: string; href: string }[];
	}>(RentripsURL + "/api/product/search/" + dValue, {
		products: [],
		users: [],
	});

	const [value, setValue] = useState(defaultValue);
	const [showPopover, setShowPopover] = useState(autoFocus);

	const debouncedEventHandler = useMemo(
		() => debounce(() => fetchData(value), 500),
		[value]
	);

	const fetchData = (input: string) => {
		if (input) {
			setDValue(input);
		}
	};

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	useEffect(() => {
		setShowPopover(autoFocus);
	}, [autoFocus]);

	useEffect(() => {
		if (eventClickOutsideDiv) {
			document.removeEventListener("click", eventClickOutsideDiv);
		}
		showPopover && document.addEventListener("click", eventClickOutsideDiv);
		return () => {
			document.removeEventListener("click", eventClickOutsideDiv);
		};
	}, [showPopover]);

	useEffect(() => {
		onChange && onChange(value);
		debouncedEventHandler();
		return () => {
			debouncedEventHandler.cancel();
		};
	}, [value]);

	useEffect(() => {
		if (showPopover && inputRef.current) {
			inputRef.current.focus();
		}
	}, [showPopover]);

	const eventClickOutsideDiv = (event: MouseEvent) => {
		if (!containerRef.current) return;
		// CLICK IN_SIDE
		if (!showPopover || containerRef.current.contains(event.target as Node)) {
			return;
		}
		// CLICK OUT_SIDE
		setShowPopover(false);
	};

	const handleSelectThing = (item: string) => {
		setValue(item);
		onInputDone && onInputDone(item);
		setShowPopover(false);
	};

	const renderRecentSearches = () => {
		return (
			<>
				<h3 className="block mt-2 sm:mt-0 px-4 sm:px-8 font-semibold text-base sm:text-lg text-neutral-800 dark:text-neutral-100">
					Pencarian terakhir
				</h3>
				<div className="mt-2">
					{["Tenda", "Grill", "Nesting"].map((item) => (
						<span
							onClick={() => handleSelectThing(item)}
							key={item}
							className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 sm:py-5 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
						>
							<span className="block text-neutral-400">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-4 sm:h-6 w-4 sm:w-6"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</span>
							<span className=" block font-medium text-neutral-700 dark:text-neutral-200">
								{item}
							</span>
						</span>
					))}
				</div>
			</>
		);
	};

	const renderSearchValue = () => {
		const products = data.products;
		const users = data.users;
		return (
			<>
				{(!products.length && users.length !== 0) || (
					<h3 className="block mt-2 sm:mt-0 px-4 sm:px-8 font-semibold text-base sm:text-lg text-neutral-800 dark:text-neutral-100">
						Produk
					</h3>
				)}

				{products.length !== 0 ? (
					products.map((item) => (
						<Link
							to={"/listing?name=" + encodeURI(item.name)}
							onClick={() => {
								logging(userInfo?.token, {
									category: "product",
									action: "search",
									target: item.name || "",
								});

								eventAnalytic({
									category: "Product",
									action: "Search",
									label: item.name,
								});
							}}
							key={item.name}
							className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 sm:py-5 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
						>
							<span className="block text-neutral-400">
								<ArchiveIcon className="h-4 w-4 sm:h-6 sm:w-6" />
							</span>
							<span className="block font-medium text-neutral-700 dark:text-neutral-200">
								{item.name}
							</span>
						</Link>
					))
				) : users.length === 0 ? (
					["Tenda", "Grill", "Kamera"].map(
						(item) =>
							item !== value && (
								<span
									onClick={() => {
										setValue(item);
										logging(userInfo?.token, {
											category: "product",
											action: "search",
											target: item || "",
										});

										eventAnalytic({
											category: "Product",
											action: "Search",
											label: item,
										});
									}}
									key={item}
									className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 sm:py-5 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
								>
									<span className="block text-neutral-400">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-4 w-4 sm:h-6 sm:w-6"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
											/>
										</svg>
									</span>
									<span className="block font-medium text-neutral-700 dark:text-neutral-200">
										{item}
									</span>
								</span>
							)
					)
				) : (
					<></>
				)}
				{users.length !== 0 && (
					<>
						<h3 className="block pt-4 sm:mt-0 px-4 sm:px-8 font-semibold text-base sm:text-lg text-neutral-800 dark:text-neutral-100 ">
							Vendor
						</h3>
						{users.map((user) => (
							<Link
								to={user.href}
								onClick={() => {
									logging(userInfo?.token, {
										category: "user",
										action: "search",
										target: user.name || "",
									});

									eventAnalytic({
										category: "User",
										action: "Search",
										label: user.name,
									});
								}}
								key={user.name}
								className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 sm:py-5 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
							>
								<span className="block text-neutral-400">
									<UserIcon className="h-4 w-4 sm:h-6 sm:w-6" />
								</span>
								<span className="block font-medium text-neutral-700 dark:text-neutral-200">
									{user.name}
								</span>
							</Link>
						))}
					</>
				)}
			</>
		);
	};

	return (
		<div className={`relative flex ${className}`} ref={containerRef}>
			<div
				onClick={() => setShowPopover(true)}
				className={`flex flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${
					showPopover ? "shadow-2xl rounded-full dark:bg-neutral-800" : ""
				}`}
			>
				<div className="text-neutral-300 dark:text-neutral-400">
					<CubeIcon className="h-4 sm:h-6 w-4 sm:w-6" />
				</div>
				<div className="flex-grow">
					<input
						className={`block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-neutral-300 xl:text-lg font-semibold placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
						placeholder={placeHolder}
						value={value}
						autoFocus={showPopover}
						onChange={(e) => setValue(e.currentTarget.value)}
						onKeyPress={(e) => {
							if (e.key === "Enter") {
								handleSelectThing(e.currentTarget.value);
							}
						}}
						ref={inputRef}
					/>
					<span className="block mt-0.5 text-sm text-neutral-400 font-light ">
						<span className="line-clamp-1">{!!value ? placeHolder : desc}</span>
					</span>
					{value && showPopover && (
						<ClearDataButton onClick={() => setValue("")} />
					)}
				</div>
			</div>
			{showPopover && (
				<div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl max-h-128 overflow-y-auto">
					{value ? renderSearchValue() : renderRecentSearches()}
				</div>
			)}
		</div>
	);
};

export default ThingInput;
